import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Trash2, Save, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtIDR } from "../lib/api";
import { PageHeader, Panel } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useVariants } from "../components/VariantPicker";

let _rid = 0;
const newRow = () => ({ key: ++_rid, variant_id: "", product_id: "", product_name: "", color: "", size: "", sku: "", qty: 1, unit_price: 0 });

export default function PurchaseOrderCreate() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const { flat } = useVariants();
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState({ supplier_id: "", est_arrival_date: "", est_finish_date: "", payment_terms: "", notes: "", discount: "", additional_cost: "" });
  const [rows, setRows] = useState([newRow()]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/suppliers", { params: { active: "active" } }).then((r) => setSuppliers(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/purchase-orders/${id}`).then((r) => {
      const po = r.data;
      setForm({ supplier_id: po.supplier_id || "", est_arrival_date: (po.est_arrival_date || "").slice(0, 10), est_finish_date: (po.est_finish_date || "").slice(0, 10), payment_terms: po.payment_terms || "", notes: po.notes || "", discount: po.discount || "", additional_cost: po.additional_cost || "" });
      setRows((po.items || []).map((it) => ({ key: ++_rid, variant_id: it.variant_id, product_id: it.product_id, product_name: it.product_name, color: it.color, size: it.size, sku: it.sku, qty: it.qty, unit_price: it.unit_price })));
    }).catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); navigate("/purchase-orders"); });
  }, [id, isEdit, navigate]);

  const setRow = (key, patch) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const addRow = () => setRows((rs) => [...rs, newRow()]);
  const delRow = (key) => setRows((rs) => (rs.length > 1 ? rs.filter((r) => r.key !== key) : rs));

  const subtotal = rows.reduce((a, r) => a + (parseInt(r.qty) || 0) * (parseInt(r.unit_price) || 0), 0);
  const total = subtotal - (parseInt(form.discount) || 0) + (parseInt(form.additional_cost) || 0);

  const save = async () => {
    if (!form.supplier_id) return toast.error("Pilih supplier");
    const items = rows.filter((r) => r.variant_id && (parseInt(r.qty) || 0) > 0).map((r) => ({
      product_id: r.product_id, variant_id: r.variant_id, product_name: r.product_name,
      color: r.color, size: r.size, sku: r.sku, qty: parseInt(r.qty) || 0, unit_price: parseInt(r.unit_price) || 0,
    }));
    if (!items.length) return toast.error("Tambahkan minimal satu item dengan qty");
    const payload = { ...form, discount: parseInt(form.discount) || 0, additional_cost: parseInt(form.additional_cost) || 0, est_arrival_date: form.est_arrival_date || null, est_finish_date: form.est_finish_date || null, items };
    setSaving(true);
    try {
      if (isEdit) await api.put(`/purchase-orders/${id}`, payload);
      else await api.post("/purchase-orders", payload);
      toast.success("Purchase Order disimpan");
      navigate("/purchase-orders");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="po-create-page">
      <PageHeader title={isEdit ? "Edit Purchase Order" : "Form Purchase Order"} subtitle="Pesanan pembelian ke supplier / konveksi">
        <Button variant="outline" onClick={() => navigate("/purchase-orders")} className="border-taupe text-cocoa" data-testid="po-back-btn"><ArrowLeft className="w-4 h-4 mr-1.5" />Kembali</Button>
      </PageHeader>

      <Panel className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
          <div><Label>Supplier / Konveksi *</Label>
            <Select value={form.supplier_id || undefined} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
              <SelectTrigger className="bg-white" data-testid="po-supplier-select"><SelectValue placeholder="Pilih supplier" /></SelectTrigger>
              <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Estimasi Datang</Label><Input type="date" value={form.est_arrival_date} onChange={(e) => setForm({ ...form, est_arrival_date: e.target.value })} className="bg-white" data-testid="po-arrival-date" /></div>
          <div><Label>Termin Pembayaran</Label><Input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} placeholder="mis. NET 30" className="bg-white" /></div>
          <div><Label>Estimasi Selesai Produksi</Label><Input type="date" value={form.est_finish_date} onChange={(e) => setForm({ ...form, est_finish_date: e.target.value })} className="bg-white" /></div>
        </div>

        <div className="border border-taupe/20 rounded-lg overflow-visible">
          <div className="grid grid-cols-[40px_2fr_1fr_1fr_90px_140px_140px_44px] items-center px-3 py-2 bg-cocoa/5 text-xs font-bold text-cocoa uppercase tracking-wide">
            <div>No</div><div>Product Name</div><div>Color</div><div>Size</div><div className="text-center">Qty</div><div>Harga Beli</div><div className="text-right">Subtotal</div><div></div>
          </div>
          {rows.map((r, i) => (
            <POItemRow key={r.key} row={r} index={i} flat={flat}
              onPick={(f) => setRow(r.key, { variant_id: f.variant_id, product_id: f.product_id, product_name: f.product_name, color: f.color, size: f.size, sku: f.sku, unit_price: f.purchase_price || 0 })}
              onQty={(q) => setRow(r.key, { qty: q })} onPrice={(p) => setRow(r.key, { unit_price: p })}
              onRemove={() => delRow(r.key)} />
          ))}
          <div className="flex justify-center py-4 border-t border-taupe/15">
            <Button onClick={addRow} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="po-add-item"><Plus className="w-4 h-4 mr-1.5" />Add Product</Button>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div><Label>Diskon (Rp)</Label><Input type="number" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} className="bg-white" data-testid="po-discount" /></div>
            <div><Label>Biaya Tambahan (Rp)</Label><Input type="number" value={form.additional_cost} onChange={(e) => setForm({ ...form, additional_cost: e.target.value })} className="bg-white" data-testid="po-addcost" /></div>
            <div><Label>Catatan</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="bg-white" /></div>
          </div>
          <div className="flex flex-col justify-end items-end text-sm space-y-1">
            <div>Subtotal: <span className="font-semibold text-cocoa">{fmtIDR(subtotal)}</span></div>
            <div>Diskon: <span className="font-semibold text-cocoa">{fmtIDR(parseInt(form.discount) || 0)}</span></div>
            <div>Biaya Tambahan: <span className="font-semibold text-cocoa">{fmtIDR(parseInt(form.additional_cost) || 0)}</span></div>
            <div className="text-lg font-bold text-cocoa" data-testid="po-total">Total: {fmtIDR(total)}</div>
          </div>
        </div>

        <div className="flex justify-end mt-5">
          <Button onClick={save} disabled={saving} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="save-po-btn"><Save className="w-4 h-4 mr-1.5" />{saving ? "Menyimpan..." : "Simpan PO"}</Button>
        </div>
      </Panel>
    </div>
  );
}

function POItemRow({ row, index, flat, onPick, onQty, onPrice, onRemove }) {
  const [q, setQ] = useState("");
  const [openList, setOpenList] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpenList(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const matches = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [];
    return flat.filter((f) => f.label.toLowerCase().includes(term)).slice(0, 30);
  }, [q, flat]);

  return (
    <div className="grid grid-cols-[40px_2fr_1fr_1fr_90px_140px_140px_44px] items-center px-3 py-2 border-t border-taupe/10 text-sm" data-testid={`po-row-${index}`}>
      <div className="text-wmuted">{index + 1}</div>
      <div className="relative pr-2" ref={boxRef}>
        {row.variant_id
          ? <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-cocoa truncate">{row.product_name}</span>
              <button className="text-xs text-blue-600 hover:underline" onClick={() => { onPick({ variant_id: "", product_id: "", product_name: "", color: "", size: "", sku: "", purchase_price: 0 }); setQ(""); setOpenList(true); }} data-testid={`po-change-product-${index}`}>ubah</button>
            </div>
          : <>
              <Input value={q} onChange={(e) => { setQ(e.target.value); setOpenList(true); }} onFocus={() => setOpenList(true)}
                placeholder="Cari nama produk..." className="h-9 bg-white" data-testid={`po-product-search-${index}`} />
              {openList && matches.length > 0 && (
                <div className="absolute z-30 mt-1 w-[320px] max-h-64 overflow-y-auto bg-white border border-taupe/30 rounded-md shadow-lg" data-testid={`po-product-options-${index}`}>
                  {matches.map((f) => (
                    <button key={f.variant_id} className="block w-full text-left px-3 py-2 text-sm hover:bg-cocoa/5"
                      onClick={() => { onPick(f); setQ(""); setOpenList(false); }} data-testid={`po-product-option-${f.variant_id}`}>{f.label}</button>
                  ))}
                </div>
              )}
              {openList && q.trim() && matches.length === 0 && (
                <div className="absolute z-30 mt-1 w-[320px] bg-white border border-taupe/30 rounded-md shadow-lg px-3 py-2 text-sm text-wmuted">Tidak ditemukan</div>
              )}
            </>}
      </div>
      <div className="text-cocoa">{row.color || "-"}</div>
      <div className="text-cocoa">{row.size || "-"}</div>
      <div className="text-center">
        <Input type="number" min={1} value={row.qty} onChange={(e) => onQty(Math.max(1, parseInt(e.target.value) || 1))} className="h-8 w-16 text-center mx-auto" data-testid={`po-qty-${index}`} />
      </div>
      <div>
        <Input type="number" min={0} value={row.unit_price} onChange={(e) => onPrice(parseInt(e.target.value) || 0)} className="h-8" data-testid={`po-price-${index}`} />
      </div>
      <div className="text-right font-medium text-cocoa">{fmtIDR((parseInt(row.qty) || 0) * (parseInt(row.unit_price) || 0))}</div>
      <div className="text-center"><button onClick={onRemove} className="text-status-reject" data-testid={`po-remove-row-${index}`}><Trash2 className="w-4 h-4" /></button></div>
    </div>
  );
}
